import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { URLS } from '@shared/utils/urls';
import { getEventTypeConfig } from '@config';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import type { EventType } from '@graphql/generated-base';
import { useEventWebsiteGuestHeadQuery } from './GuestSiteHead.graphql.generated';
import { generateDescriptionKeywords } from '@shared/utils/seo';
import { Seo } from '@shared/components/Seo';
import { useTranslation } from '@shared/core';
import { extractEventPhoto } from '@shared/utils/eventPhoto';

/**
 * Adds a query param to the image url to optimize the image from Media Service and add a source query param
 */
const addOptimizationQueryParam = (url: Maybe<string>): string | undefined => {
  if (!url) return undefined;
  const hasQueryParam = url.includes('?');
  const separator = hasQueryParam ? '&' : '?';
  return `${url}${separator}rendition=small&source=websitepreview`;
};

interface Props
  extends Readonly<{
    handle: Maybe<string>;
    eventDisplayName: Maybe<string>;
    imageUrl: Maybe<string>;
    hideFromSearchEngines: boolean;
    ownerFirstName: Maybe<string>;
    ownerLastName: Maybe<string>;
    fianceeFirstName: Maybe<string>;
    pageData: EventPageFragment[];
    eventType: EventType;
  }> {}

const Root = (props: Props) => {
  const { handle, eventDisplayName, imageUrl, hideFromSearchEngines, ownerFirstName, ownerLastName, fianceeFirstName, pageData, eventType } = props;
  const { t } = useTranslation('websitePages');

  const keywords = useMemo(() => generateDescriptionKeywords(t, pageData), [t, pageData]);
  const userInfo = useMemo(
    () => ({
      partnerOneFirstName: ownerFirstName || '',
      partnerOneLastName: ownerLastName || '',
      partnerTwoFirstName: fianceeFirstName || '',
    }),
    [ownerFirstName, ownerLastName, fianceeFirstName],
  );

  const pageTitle = useMemo(() => getEventTypeConfig(eventType).guestSitePageTitle(eventDisplayName || '', userInfo, t) || '', [eventType, eventDisplayName, userInfo, t]);

  const pageDescription = useMemo(
    () => getEventTypeConfig(eventType).guestSitePageDescription(eventDisplayName || '', userInfo, keywords, t),
    [eventType, eventDisplayName, userInfo, keywords, t],
  );
  const pageUrl = `https://withjoy.com/${handle}/`;

  return (
    <>
      <Seo
        title={pageTitle}
        description={pageDescription}
        canonicalURL={pageUrl}
        name="Joy"
        url={pageUrl}
        ogImage={addOptimizationQueryParam(imageUrl)}
        author="@JoytheApp"
        twitterSite="@JoytheApp"
      />
      <Helmet>
        <link
          href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital@0;1&display=swap"
          rel="stylesheet"
        />
        <script src={URLS.styleApplicatorLib} />
        {hideFromSearchEngines && (
          <meta
            name="robots"
            content="noindex"
          />
        )}
      </Helmet>
    </>
  );
};
/**
 * This supplies \<Helmet /> from react-helmet-async
 * Provides SEO meta tags and title information
 * Note: Font preloading is now handled by FontPackProvider
 */
export const GuestSiteHead: React.FC<{ eventHandle: string }> = props => {
  const { data } = useEventWebsiteGuestHeadQuery({
    variables: { eventHandle: props.eventHandle },
    ssr: true,
    fetchPolicy: 'cache-first',
    batchMode: 'off',
  });

  const headData = useMemo(() => {
    const event = data?.eventByName;
    const eventPhoto = extractEventPhoto(event);

    return event
      ? {
          hideFromSearchEngines: event.settings.hideFromSearchEngines,
          eventDisplayName: event.info.eventDisplayName,
          imageUrl: eventPhoto?.url,
          handle: event.website,
          ownerFirstName: event.info.ownerFirstName,
          ownerLastName: event.info.ownerLastName,
          fianceeFirstName: event.info.fianceeFirstName,
          pageData: event.pages,
          eventType: event.info.eventType,
        }
      : undefined;
  }, [data]);

  return headData ? <Root {...headData} /> : null;
};
